import * as React from "react"

import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../cn"

const avatarVariants = cva(
  [
    "inline-flex shrink-0 items-center justify-center overflow-hidden",
    "rounded-full select-none font-medium",
  ],
  {
    variants: {
      size: {
        sm: "h-5 w-5 text-xs",
        md: "h-8 w-8 text-xs",
        lg: "h-10 w-10 text-sm",
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
)

/**
 * Takes the first letter of each of the first two words, so "Acme Robotics"
 * reads "AR" and a single-word name reads as one letter.
 */
function initialsFrom(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? "")
    .join("")
}

export interface AvatarProps
  extends
    Omit<React.HTMLAttributes<HTMLSpanElement>, "children">,
    VariantProps<typeof avatarVariants> {
  /** The entity this avatar stands for. Drives the initials and the alt text. */
  name: string
  /** Falls back to initials when absent, or once the image fails to load. */
  src?: string | null
  /**
   * Backdrop behind the image or the initials, as a CSS color. Callers own the
   * policy — a persona derives one from its id, an organization takes the
   * neutral default.
   */
  tint?: string
  /**
   * Hides the avatar from assistive tech. Set this where the name is already
   * rendered as text beside it, so a screen reader does not read it twice.
   */
  decorative?: boolean
}

const Avatar = React.forwardRef<HTMLSpanElement, AvatarProps>(
  (
    { className, size, name, src, tint, decorative = false, style, ...props },
    ref,
  ) => {
    const [hasImageFailed, setHasImageFailed] = React.useState(false)

    // A new src deserves its own attempt: the element is reused across
    // messages, and one broken URL must not suppress the next one.
    React.useEffect(() => {
      setHasImageFailed(false)
    }, [src])

    const showImage = Boolean(src) && !hasImageFailed

    return (
      <span
        ref={ref}
        role={decorative ? undefined : "img"}
        aria-label={decorative ? undefined : name}
        aria-hidden={decorative ? true : undefined}
        className={cn(avatarVariants({ size }), className)}
        style={{
          backgroundColor: tint ?? "var(--coval-avatar-8)",
          // Applied inline, like the tint: neither token is exposed as a
          // Tailwind utility, and both stay put across themes.
          color: "var(--coval-avatar-ink)",
          ...style,
        }}
        {...props}
      >
        {showImage ? (
          <img
            src={src ?? ""}
            alt=""
            className="h-full w-full object-cover"
            onError={() => setHasImageFailed(true)}
          />
        ) : (
          <span className="flex h-full w-full items-center justify-center leading-none">
            {initialsFrom(name)}
          </span>
        )}
      </span>
    )
  },
)
Avatar.displayName = "Avatar"

export { Avatar, avatarVariants }
